import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useUser, useAuth as useClerkAuth, useClerk, useReverification } from '@clerk/clerk-react';
import { API_URL } from '../config';
import { readDeletion, parseDeletion, isDeletionGuard, deletionRequest } from '../services/accountDeletion';

const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }

export function AuthProvider({ children }) {
  const clerk = useUser();
  // Account changes discard requests and deletion state; refresh preserves drafts.
  return <AuthSession key={clerk.isSignedIn ? clerk.user?.id : 'signed-out'} {...clerk}>{children}</AuthSession>;
}

function AuthSession({ children, isLoaded, isSignedIn, user }) {
  const { getToken } = useClerkAuth();
  const clerkClient = useClerk();
  const { signOut } = clerkClient;
  const [session, setSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [retry, setRetry] = useState(0);
  const [deletion, setDeletion] = useState(null);
  const profileFetched = useRef(false);
  const alive = useRef(true);
  const deletionRef = useRef(null);
  const busy = useRef(false);
  const generation = useRef(0);
  const token = session?.token || null;
  useEffect(() => { alive.current = true; return () => { alive.current = false; generation.current++; }; }, []);
  const updateDeletion = useCallback(value => {
    deletionRef.current = value;
    setDeletion(value);
    if (value) setCurrentUser(null);
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let active = true;
    let request = 0;
    async function refreshToken() {
      const id = ++request;
      try {
        const jwt = await getToken();
        if (!active || id !== request) return;
        if (!jwt) {
          setSession(null); setCurrentUser(null); profileFetched.current = false;
          setSessionError('Your session has expired. Retry your session or sign in again.');
          setReady(true);
          return;
        }
        setSession({ token: jwt });
        setSessionError('');
      } catch {
        if (!active || id !== request) return;
        setSessionError('We could not refresh your session. Check your connection and retry.');
        setReady(true);
      }
    }
    refreshToken();
    const interval = setInterval(refreshToken, 50000);
    return () => { active = false; clearInterval(interval); };
  }, [isLoaded, isSignedIn, getToken, retry]);

  useEffect(() => {
    if (!token || deletionRef.current) return;
    const controller = new AbortController();
    const version = generation.current;
    const active = () => !controller.signal.aborted && version === generation.current;
    async function fetchProfile() {
      let statusVerified = false;
      try {
        // No provision-capable profile read until the owned status endpoint
        // explicitly reports that no operation exists. Unknown/legacy APIs stop here.
        const operation = await readDeletion(token, controller.signal);
        if (!active()) return;
        if (operation) { updateDeletion({ kind: 'requested', operation, accepted: true }); return; }
        statusVerified = true;
        if (profileFetched.current) return;
        const response = await fetch(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const profile = await response.json();
        if (!active()) return;
        if (isDeletionGuard(response, profile)) {
          updateDeletion({ kind: 'uncertain', accepted: true });
          return;
        }
        if (!response.ok) throw new Error('Profile unavailable');
        setCurrentUser(profile); profileFetched.current = true;
      } catch {
        if (active()) {
          if (profileFetched.current) {
            setSessionError('We could not check account status. Check your connection and retry.');
            return;
          }
          setCurrentUser(null);
          if (!statusVerified) updateDeletion({ kind: 'unavailable', error: 'We could not verify account status. Check again before continuing.' });
        }
      } finally {
        if (active()) setReady(true);
      }
    }
    fetchProfile();
    return () => controller.abort();
  }, [token, session, updateDeletion]);

  async function refreshDeletion() {
    if (!token || busy.current) return;
    busy.current = true;
    const previous = deletionRef.current;
    const version = ++generation.current;
    updateDeletion({ ...previous, kind: 'checking', error: '' });
    try {
      const operation = await readDeletion(token);
      if (!alive.current || version !== generation.current) return;
      if (operation) updateDeletion({ kind: 'requested', operation, accepted: true });
      else if (previous?.attempted || previous?.accepted) {
        // Absence can race an in-flight request; never restore ordinary UI here.
        updateDeletion({ ...previous, kind: 'uncertain', canRetry: !previous.accepted,
          error: previous.accepted
            ? 'The service did not return the saved operation. Keep checking status; account actions remain paused.'
            : 'No saved request was returned. An earlier request may still be in flight. Check again or retry the same deletion request.' });
      } else {
        updateDeletion(null); profileFetched.current = false; setSession(value => ({ ...value }));
      }
    } catch {
      if (alive.current && version === generation.current) updateDeletion({ ...previous, kind: 'uncertain', error: 'Status could not be checked. This does not confirm completion. Try again with a valid session.' });
    } finally { if (alive.current && version === generation.current) busy.current = false; }
  }

  const sendIntent = useReverification(async attempt => {
    const ownsIdentity = () => alive.current && attempt.version === generation.current
      && Boolean(user?.id) && clerkClient.user?.id === user.id;
    if (!ownsIdentity()) throw new Error('Account changed');
    const jwt = await getToken({ skipCache: true });
    if (!ownsIdentity() || !jwt) throw new Error('Session unavailable');
    const {response, body} = await deletionRequest(`${API_URL}/profile`, {
      method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ confirmation: 'delete_account' }),
    }, async response => ({response,body:await response.json()}));
    if (!ownsIdentity()) throw new Error('Account changed');
    if (response.status === 403 && body?.error === 'reverification_required' && !attempt.challenged) {
      attempt.challenged = true;
      // Translate our explicit Worker error into the installed Clerk hook's hint.
      // second_factor selects the strongest available factor, falling back to
      // first for users without MFA. The backend alone enforces its reviewed window.
      return { clerk_error: { type: 'forbidden', reason: 'reverification-error', metadata: {
        reverification: { level: 'second_factor', afterMinutes: 10 },
      } } };
    }
    return { response, body };
  });

  async function submitDeletion() {
    if (!token || busy.current || deletionRef.current?.accepted) return;
    busy.current = true;
    const version = ++generation.current;
    updateDeletion({ kind: 'submitting', attempted: true });
    try {
      const result = await sendIntent({ version, challenged: false });
      if (!alive.current || version !== generation.current) return;
      if (result?.response?.status === 202) {
        updateDeletion({ kind: 'requested', operation: parseDeletion(result.body), accepted: true });
      } else if (isDeletionGuard(result?.response || {}, result?.body)) {
        updateDeletion({ kind: 'uncertain', accepted: true });
      } else {
        const code = result?.body?.error;
        const message = code === 'reverification_required'
          ? 'Verification did not satisfy the account deletion requirement. Check status, then retry verification.'
          : ['identity_configuration', 'identity_permission'].includes(code)
            ? 'Account deletion is unavailable because the service identity configuration needs review. No completion is confirmed.'
            : 'The service did not confirm a saved deletion request. Check status before retrying.';
        updateDeletion({ kind: 'uncertain', attempted: true, canRetry: true, error: message });
      }
    } catch (error) {
      if (alive.current && version === generation.current) updateDeletion({ kind: 'uncertain', attempted: true, canRetry: true,
        error: error?.code === 'reverification_cancelled'
          ? 'Verification was cancelled. Deletion is not confirmed; check status before retrying.'
          : 'The deletion response was not confirmed. Check status before retrying. A saved request continues on the server.' });
    } finally { if (alive.current && version === generation.current) busy.current = false; }
  }

  // A guard reported by an ordinary account API must close every protected page.
  const reportDeletion = useCallback(() => {
    if (!alive.current) return;
    generation.current++; busy.current = false;
    updateDeletion({ kind: 'uncertain', accepted: true });
  }, [updateDeletion]);

  function logout(callback) {
    return signOut().then(() => {
      if (!alive.current) return;
      setSession(null); setCurrentUser(null);
      if (typeof callback === 'function') callback();
    }).catch(() => { if (alive.current) setSessionError('We could not sign you out. Check your connection and try again.'); });
  }

  return <AuthContext.Provider value={{
    currentUser, clerkUser: user, token, sessionError, deletion,
    refreshDeletion, submitDeletion, reportDeletion,
    isAuthenticated: isSignedIn,
    isLoading: !isLoaded || (isSignedIn && !ready),
    retrySession: () => { if (deletionRef.current && token) refreshDeletion(); setRetry(value => value + 1); }, logout,
    login: () => console.warn('login() is deprecated - use Clerk components'),
    register: () => console.warn('register() is deprecated - use Clerk components'),
  }}>{children}</AuthContext.Provider>;
}
